// src/pages/recap/RecapPage.jsx
// ---------------------------------------------------------------------
// Quick bilingual parent-recap generator. No match doc required — the
// coach types in the opponent + final score and Claude writes a short
// parent-friendly message in English AND Spanish in ~2 seconds.
//
// Backed by the `generateMatchSummary` Cloud Function's rawInputs branch
// (see functions/modules/matchSummary.js). Persists to /recaps so the
// coach can see a short history and reopen previous recaps.
// ---------------------------------------------------------------------
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  collection,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';

import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import ParentSummaryModal from '@/components/match/ParentSummaryModal';

import {
  SparklesIcon,
  TrophyIcon,
  InformationCircleIcon,
  ClockIcon,
  TrashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

// Sport values stay in English so they map cleanly on the Cloud Function side;
// labels are localized at render time from `recap.sports.*`.
const SPORT_VALUES = [
  { value: '', labelKey: 'recap.form.sportSelect' },
  { value: 'Soccer', labelKey: 'recap.sports.soccer' },
  { value: 'Basketball', labelKey: 'recap.sports.basketball' },
  { value: 'Football', labelKey: 'recap.sports.football' },
  { value: 'Baseball', labelKey: 'recap.sports.baseball' },
  { value: 'Softball', labelKey: 'recap.sports.softball' },
  { value: 'Hockey', labelKey: 'recap.sports.hockey' },
  { value: 'Volleyball', labelKey: 'recap.sports.volleyball' },
  { value: 'Lacrosse', labelKey: 'recap.sports.lacrosse' },
  { value: 'Rugby', labelKey: 'recap.sports.rugby' },
  { value: 'Other', labelKey: 'recap.sports.other' },
];

const NOTES_MAX = 600;

function todayIso() {
  return format(new Date(), 'yyyy-MM-dd');
}

function resultKey(ourScore, theirScore) {
  const us = Number(ourScore);
  const them = Number(theirScore);
  if (!Number.isFinite(us) || !Number.isFinite(them)) return 'played';
  if (us > them) return 'won';
  if (us < them) return 'lost';
  return 'tied';
}

function useRelativeTime() {
  const { t, i18n } = useTranslation();
  return (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
      if (!d) return '';
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 1) return t('recap.history.justNow');
      if (mins < 60) return t('recap.history.minutesAgo', { count: mins });
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return t('recap.history.hoursAgo', { count: hrs });
      const days = Math.floor(hrs / 24);
      if (days < 7) return t('recap.history.daysAgo', { count: days });
      const locale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d);
    } catch {
      return '';
    }
  };
}

export default function RecapPage() {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const { firebaseUser } = useAuth();
  const { currentTeam } = useTeam();
  const uid = firebaseUser?.uid;

  const [form, setForm] = useState({
    teamName: currentTeam?.name || '',
    opponentName: '',
    ourScore: '',
    theirScore: '',
    sport: currentTeam?.sport || '',
    date: todayIso(),
    notes: '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [preloaded, setPreloaded] = useState(null); // { summaryEn, summaryEs } when reopening from history

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Re-sync teamName if the active team changes while page is open
  useEffect(() => {
    if (currentTeam?.name && !form.teamName) {
      setForm((f) => ({ ...f, teamName: currentTeam.name }));
    }
    if (currentTeam?.sport && !form.sport) {
      setForm((f) => ({ ...f, sport: currentTeam.sport }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam?.id]);

  // Subscribe to the user's recent recaps
  useEffect(() => {
    if (!uid) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    const q = query(
      collection(db, 'recaps'),
      where('uid', '==', uid),
      orderBy('createdAt', 'desc'),
      fsLimit(6)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setHistoryLoading(false);
      },
      (err) => {
        console.error('[RecapPage] history error', err);
        setHistoryLoading(false);
      }
    );
    return unsub;
  }, [uid]);

  const rawInputs = useMemo(() => {
    const dateStr = form.date
      ? format(new Date(`${form.date}T00:00:00`), 'EEEE, MMMM d')
      : '';
    return {
      teamName: form.teamName.trim(),
      opponentName: form.opponentName.trim(),
      ourScore: Number(form.ourScore),
      theirScore: Number(form.theirScore),
      sport: form.sport.trim(),
      dateStr,
      notes: form.notes.trim(),
    };
  }, [form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'notes' && value.length > NOTES_MAX) return;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const canSubmit =
    form.teamName.trim().length > 0 &&
    form.opponentName.trim().length > 0 &&
    form.ourScore !== '' &&
    form.theirScore !== '' &&
    Number.isFinite(Number(form.ourScore)) &&
    Number.isFinite(Number(form.theirScore));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(t('recap.form.validationError'));
      return;
    }
    setPreloaded(null);
    setModalOpen(true);
  };

  const openFromHistory = (recap) => {
    setPreloaded({
      summaryEn: recap.summaryEn || '',
      summaryEs: recap.summaryEs || '',
    });
    // Also reflect the historical inputs in the form so the label reads right
    setForm((f) => ({
      ...f,
      teamName: recap.teamName || f.teamName,
      opponentName: recap.opponentName || '',
      ourScore: recap.ourScore != null ? String(recap.ourScore) : '',
      theirScore: recap.theirScore != null ? String(recap.theirScore) : '',
    }));
    setModalOpen(true);
  };

  const deleteFromHistory = async (recapId) => {
    try {
      await deleteDoc(doc(db, 'recaps', recapId));
      toast.success(t('recap.history.deleted'));
    } catch (err) {
      console.error('[RecapPage] delete failed', err);
      toast.error(t('recap.history.deleteError'));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* ---- Header ---- */}
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-900/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-3">
          <SparklesIcon className="h-3 w-3" />
          {t('recap.badge')}
        </div>
        <Heading level={1}>{t('recap.heading')}</Heading>
        <Text>{t('recap.description')}</Text>
      </div>

      {/* ---- Form card ---- */}
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm p-5 sm:p-6 space-y-5 max-w-2xl"
      >
        {/* Your team */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
            {t('recap.form.yourTeam')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="teamName"
            value={form.teamName}
            onChange={handleChange}
            placeholder={t('recap.form.yourTeamPlaceholder')}
            className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            required
          />
        </div>

        {/* Opponent + Scores row */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:items-end">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {t('recap.form.opponent')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="opponentName"
              value={form.opponentName}
              onChange={handleChange}
              placeholder={t('recap.form.opponentPlaceholder')}
              className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex items-end gap-2">
            <div className="w-24">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                {t('recap.form.us')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                name="ourScore"
                value={form.ourScore}
                onChange={handleChange}
                placeholder="3"
                className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-center text-lg font-bold text-zinc-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <span className="pb-3 text-zinc-400 font-bold">–</span>
            <div className="w-24">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                {t('recap.form.them')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                name="theirScore"
                value={form.theirScore}
                onChange={handleChange}
                placeholder="2"
                className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-center text-lg font-bold text-zinc-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Sport + Date row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {t('recap.form.sport')}
            </label>
            <select
              name="sport"
              value={form.sport}
              onChange={handleChange}
              className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-sm text-zinc-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {SPORT_VALUES.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {t('recap.form.matchDate')}
            </label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-sm text-zinc-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {t('recap.form.notes')} <span className="text-zinc-400 font-normal">{t('recap.form.notesOptional')}</span>
            </label>
            <span className="text-[11px] text-zinc-400">
              {form.notes.length}/{NOTES_MAX}
            </span>
          </div>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
            placeholder={t('recap.form.notesPlaceholder')}
            className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
          <p className="mt-1 text-[11px] text-zinc-400">{t('recap.form.notesHelp')}</p>
        </div>

        {/* Explainer */}
        <div className="flex gap-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800 p-3.5 text-xs text-zinc-600 dark:text-zinc-400">
          <InformationCircleIcon className="h-4 w-4 shrink-0 mt-0.5 text-zinc-400" />
          <p className="leading-relaxed">{t('recap.form.disclaimer')}</p>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-1">
          <Button color="blue" type="submit" disabled={!canSubmit}>
            <TrophyIcon className="size-4 mr-1.5" />
            {t('recap.form.submit')}
          </Button>
        </div>
      </form>

      {/* ---- Recent Recaps ---- */}
      <div className="mt-8 max-w-2xl">
        <div className="flex items-center gap-2 mb-3">
          <ClockIcon className="h-4 w-4 text-zinc-400" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {t('recap.history.title')}
          </h2>
        </div>

        {historyLoading ? (
          <div className="space-y-2">
            {[1, 2].map((n) => (
              <div key={n} className="h-16 rounded-xl bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-zinc-200 dark:border-zinc-700 p-6 text-center">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {t('recap.history.empty')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((r) => {
              const rkey = resultKey(r.ourScore, r.theirScore);
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-3 rounded-xl border border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-all group"
                >
                  <button
                    type="button"
                    onClick={() => openFromHistory(r)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-zinc-900 dark:text-white truncate">
                        {r.teamName || t('recap.history.yourTeamFallback')}
                      </span>
                      <span className="text-xs text-zinc-400">vs</span>
                      <span className="text-sm font-semibold text-zinc-900 dark:text-white truncate">
                        {r.opponentName || t('recap.history.opponentFallback')}
                      </span>
                      <span
                        className={
                          'ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ' +
                          (rkey === 'won'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : rkey === 'lost'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                            : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400')
                        }
                      >
                        {r.ourScore != null && r.theirScore != null
                          ? `${r.ourScore}–${r.theirScore}`
                          : t(`recap.history.result${rkey.charAt(0).toUpperCase() + rkey.slice(1)}`)}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                      {r.dateStr ? `${r.dateStr} · ` : ''}
                      {relativeTime(r.createdAt)}
                      {r.notes ? ` · ${t('recap.history.withNotes')}` : ''}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteFromHistory(r.id)}
                    className="shrink-0 p-2 rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors opacity-0 group-hover:opacity-100"
                    aria-label={t('recap.history.deleteAria')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openFromHistory(r)}
                    className="shrink-0 p-1 text-zinc-400 group-hover:text-blue-500 transition-colors"
                    aria-label={t('recap.history.openAria')}
                  >
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Modal ---- */}
      <ParentSummaryModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setPreloaded(null);
        }}
        rawInputs={preloaded ? null : rawInputs}
        preloaded={preloaded}
        teamName={form.teamName}
      />
    </div>
  );
}
